<template>
  <layout
    title="Compliance, age verification and fraud prevention | Youverse"
    :transparent="true"
    class="overflow-x-hidden gamingSection"
  >
    <highlightSection
      backgroundImage="/storage/images/industries/igaming/report-gaming-header.jpg"
      altImage="Accelerate onboarding"
      title="1 in 4 of your players is fake."
      subtitle="Fight back bonus abuse, fake accounts, and chargeback fraud while maintaining player privacy and seamless gameplay."
      leftButton="Get started"
      urlLeftButton="#"
      urlRightButton="https://6992654.fs1.hubspotusercontent-na1.net/hubfs/6992654/01.%20ONE%20PAGERS/Youverse_Gaming.pdf"
      rightButton="See how it works"
    ></highlightSection>

    <modalForm :show="showFormTalkToUs" @closeModal="closeModalTalkToUs">
      <talkToUs @closeModalTalkToUs="closeModalTalkToUs" :errors="errors" />
    </modalForm>

    <div
      class="w-full mx-auto px-6 sm:px-7 md:px-4 lg:px-10 xl:px-16 max-w-screen-sm md:max-w-screen-xl pt-12"
    >
      <h2
        class="text-3xl font-bold text-center mb-14 text-gray-900 px-4 sm:px-6 md:px-8 lg:px-32"
      >
        How Youverse protects your platform
      </h2>
      <rowCardsSection :elements="secureFeatures" />
      <rowCardsSection :elements="secureFeatures2" class="mt-8" />
    </div>

    <div
      class="w-full mx-auto px-6 sm:px-7 md:px-4 lg:px-10 xl:px-16 max-w-screen-sm md:max-w-screen-xl pt-16 flex gap-12 items-center flex-col"
    >
      <h2
        class="text-3xl font-bold text-center text-gray-900 px-4 sm:px-6 md:px-8 lg:px-32"
      >
        Certified security and trust
      </h2>
      <p class="max-w-[800px] text-center">
        Youverse is ISO/IEC 30107-3 Level 1 and 2 PAD certified by iBeta,
        proving our liveness detection meets the highest global standards
        against spoofing attacks.
      </p>
      <certificates />
    </div>

    <FeatureHighlightSection
      :features="gamblingFeatures"
      title="Why gaming and gambling operators choose Youverse"
    />
    <div class="relative max-w-screen-xl m-auto">
      <FeatureHighlightSection
        :features="protectionFeatures"
        title="Full journey protection"
        :imgRight="true"
      />
      <img
        src="/storage/images/industries/igaming/lateral-circles.png"
        alt="lateral-circles"
        class="absolute md:top-0 top-28 right-40 -z-10 md:h-full h-1/2 opacity-50"
      />
    </div>
    <div class="relative max-w-screen-xl m-auto">
      <FeatureHighlightSection
        :features="operatorFeatures"
        title="Tailored solutions for every operator"
      />
      <img
        src="/storage/images/industries/igaming/lateral-circles.png"
        alt="lateral-circles"
        class="absolute md:top-0 top-28 left-40 -z-10 md:h-full h-1/2 opacity-50 scale-x-[-1]"
      />
    </div>

    <div
      class="flex flex-col items-center gap-12 mt-12 max-w-screen-md m-auto px-4"
    >
      <h2 class="text-2xl font-bold">For developers</h2>
      <p class="text-center">
        Built with developers in mind. Explore our SDKs, APIs, and technical
        documentation to integrate Youverse quickly and efficiently.
      </p>
      <div class="flex gap-12">
        <a
          href="https://github.com/dev-yoonik"
          target="_blank"
          aria-label="GitHub"
        >
          <font-awesome-icon :icon="['fab', 'github']" size="3x" />
        </a>
        <a
          href="https://discord.gg/SqHVQUFNtN"
          target="_blank"
          aria-label="Discord"
          style="color: #5865f2"
        >
          <font-awesome-icon :icon="['fab', 'discord']" size="3x" />
        </a>
      </div>
      <p class="text-center">
        Join our developer community on <a>GitHub</a> and <a>Discord</a> to get
        support, share feedback, and build better integrations.
      </p>
    </div>

    <div class="pt-20">
      <cta
        title="Stop losing money to fake players. Build trust with real ones."
        buttonText="Get started"
        buttonLink="/book-a-meeting"
        target="_blank"
      />
    </div>

    <faq group="gaming" />
  </layout>
</template>

<script>
const layout = () =>
  import(/* webpackChunkName: "layout" */ "../../Components/Layout/layout.vue");

const highlightSection = () =>
  import(
    /* webpackChunkName: "highlightSection" */ "../../Components/Layout/highlightSection.vue"
  );

const EndToEnd = () =>
  import(
    /* webpackChunkName: "ProductsInfoCards" */ "../../Components/Layout/UseCases/EndToEndSection.vue"
  );

const FeatureHighlightSection = () =>
  import(
    /* webpackChunkName: "FeatureHighlightSection" */ "../../Components/Layout/UseCases/FeatureHighlightSection.vue"
  );

const cta = () =>
  import(/* webpackChunkName: "cta" */ "../../Components/Layout/cta.vue");

const rowCardsSection = () =>
  import(
    /* webpackChunkName: "rowCardsSection" */ "../../Components/Layout/rowCardsSection.vue"
  );

const partners = () =>
  import(
    /* webpackChunkName: "partners" */ "../../Components/Layout/HomePage/partners.vue"
  );

const latestPosts = () =>
  import(
    /* webpackChunkName: "latestPosts" */ "../../Components/Layout/latestPosts.vue"
  );

const certificates = () =>
  import(
    /* webpackChunkName: "certificates" */ "../../Components/Layout/Products/certificates.vue"
  );

const faq = () =>
  import(
    /* webpackChunkName: "FaqSection" */ "../../Components/Layout/FaqSection.vue"
  );

const talkToUs = () =>
  import(
    /* webpackChunkName: "FormTalkToUsGaming" */ "../../Components/Layout/UseCases/FormTalkToUsGaming.vue"
  );
const modalForm = () =>
  import(/* webpackChunkName: "modal" */ "../../Components/modal.vue");

export default {
  props: {
    errors: Object,
  },
  components: {
    layout,
    highlightSection,
    EndToEnd,
    FeatureHighlightSection,
    cta,
    rowCardsSection,
    partners,
    latestPosts,
    certificates,
    faq,
    talkToUs,
    modalForm,
  },
  mounted() {
    this.$nextTick(() => {
      this.clickEvent = document.addEventListener("click", (event) => {
        const container = document.getElementsByClassName("gamingSection");
        const clickedHeaderButton = event.target.closest(
          ".highlightLeftButton"
        );
        const clickCta = event.target.closest(".cta");

        if (
          container.length > 0 &&
          ((clickedHeaderButton &&
            container[0].contains(clickedHeaderButton)) ||
            (clickCta && container[0].contains(clickCta)))
        ) {
          event.preventDefault();
          this.showModalFormTalkToUs();
        }
      });
    });
  },
  beforeDestroyed() {
    window.removeEventListener("click", this.clickEvent);
  },
  data() {
    return {
      showFormTalkToUs: false,
      clickEvent: null,
      endToEnd: [
        {
          title: "Onboarding",
          iconPath: "/storage/images/icons/onboarding.svg",
          description: [
            "ID verification",
            "Document check",
            "Liveness and face match",
          ],
        },
        {
          title: "Compliance",
          iconPath: "/storage/images/icons/compliance.svg",
          description: ["AML", "Self Exclusion"],
        },
        {
          title: "Login",
          iconPath: "/storage/images/icons/login.svg",
          description: [
            "Passwordless login",
            "Multi-factor authentication",
            "Password recovery",
          ],
        },
        {
          title: "Fraud Detection",
          iconPath: "/storage/images/icons/fraud.svg",
          description: [
            "Bot/multiple account detection",
            "Account takeover checks",
            "Payment authorization",
          ],
        },
      ],
      secureFeatures: [
        {
          title: `Stop fake accounts`,
          text: `Our liveness detection technology prevents bots, deepfakes, and replay attacks, ensuring a secure and authentic player base.`,
          imageSrc: `/storage/images/icons/user-shield.svg`,
          imageAlt: `Identity Verification`,
          imageStyle: `w-14 h-14`,
        },
        {
          title: `Keep minors out`,
          text: `Our age verification checks and blocks instantly underage users, protecting minors and helping you stay compliant in highly regulated markets.`,
          imageSrc: `/storage/images/icons/face_authentication_light.svg`,
          imageAlt: `Identity Verification`,
          imageStyle: `w-14 h-14`,
        },
        {
          title: `Stop multi-accounting`,
          text: `Face matching helps preventing  duplicate registrations and protect against fraudulent activity.`,
          imageSrc: `/storage/images/icons/identity-fraud.svg`,
          imageAlt: `Identity Verification`,
          imageStyle: `w-14 h-14`,
        },
      ],
      secureFeatures2: [
        {
          title: `Verify real IDs`,
          text: `Global document verification authenticates passports, IDs, and driver’s licenses in real time with advanced OCR.`,
          imageSrc: `/storage/images/icons/mobility_identity_verification.svg`,
          imageAlt: `Identity Verification`,
          imageStyle: `w-14 h-14`,
        },
        {
          title: `Stay compliant`,
          text: `Screen players against PEP, sanctions, and watchlists automatically to meet AML and regulatory requirements without friction.`,
          imageSrc: `/storage/images/icons/shield-star.svg`,
          imageAlt: `Identity Verification`,
          imageStyle: `w-14 h-14`,
        },
        {
          title: `Integrate in hours`,
          text: `Our lightweight APIs and SDKs make setup fast and easy, so your team can go live without heavy development effort.`,
          imageSrc: `/storage/images/icons/settings-bolt.svg`,
          imageAlt: `Identity Verification`,
          imageStyle: `w-14 h-14`,
        },
      ],
      gamblingFeatures: [
        {
          title: "Cost-effective fraud prevention",
          description:
            "Prevent bonus abuse, synthetic identities, and account takeovers that cost operators millions annually while reducing manual review overhead.",
          image: "/storage/images/industries/igaming/regulatory-compliance.png",
        },
        {
          title: "Regulatory compliance made simple",
          description:
            "Meet KYC/AML checks, PEPs & sanctions requirements, and gambling regulations across jurisdictions with ease.",
          image: "/storage/images/industries/igaming/reduce-player.png",
        },
        {
          title: "Reduce player drop-off rates",
          description:
            "Our streamlined verification delivers a False Rejection Rate of 0.4% (about 4 in 1,000 attempts) balancing player experience and security.",
          image: "/storage/images/industries/igaming/cost-effective.png",
        },
      ],
      protectionFeatures: [
        {
          title: "Onboard to withdraw",
          description:
            "Protection doesn’t stop at registration. Continual checks span deposit, play, withdrawal, and every interaction in between.",
          image: "/storage/images/industries/igaming/report-gaming-dice.png",
        },
        {
          title: "Continuous authentication",
          description:
            "Ongoing liveness and identity checks ensure that the same verified player is the one behind every session, preventing account takeover.",
          image: "/storage/images/industries/igaming/report-gaming-dice.png",
        },
        {
          title: "Responsible gaming",
          description:
            "Combine verification with responsible gaming features, helping operators identify vulnerable players and comply with safer gaming rules.",
          image: "/storage/images/industries/igaming/report-gaming-dice.png",
        },
      ],
      operatorFeatures: [
        {
          title: "For operators and platform providers",
          description:
            "Whether you run a single brand or power multiple operators, Youverse scales to your needs and integrates seamlessly.",
          image: "/storage/images/industries/igaming/report-gaming-cards.png",
        },
        {
          title: "Modular and flexible",
          description:
            "Pick and choose only the modules you need (liveness, age verification, documents, compliance) and scale as your business grows.",
          image: "/storage/images/industries/igaming/report-gaming-cards.png",
        },
        {
          title: "Enterprise-grade security",
          description:
            "Benefit from top-tier encryption and decentralized architecture that keep both your business and your players safe.",
          image: "/storage/images/industries/igaming/report-gaming-cards.png",
        },
      ],
    };
  },
  methods: {
    closeModalTalkToUs() {
      this.showFormTalkToUs = false;
    },
    showModalFormTalkToUs() {
      this.showFormTalkToUs = true;
    },
  },
};
</script>
